import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Loader,
  Message,
  Panel,
  Row,
  SelectPicker,
  Stack,
} from "rsuite";
import AdminDashboard from "../../components/AdminDashboard";
import { DriverStatus } from "../../my-enum";
import { Driver, User, Vehicle } from "../../my-type";
import { authUserData } from "../../service";
import {
  approveApply,
  disableDriver,
  getDriver,
  getDriverPhoto,
  updateDriver,
  uploadDriverPhoto,
} from "../../service/DriverService";
import UserService from "../../service/UserService";

type DriverPhotoUrls = {
  licensePhotoUrl: string | null;
  motorLicensePhotoUrl: string | null;
  insurancePhotoUrl: string | null;
};

function toDateInputValue(value?: string | Date | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }
  return parsed.toISOString().split("T")[0];
}

function toIsoDateOrNull(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  return new Date(`${trimmed}T00:00:00.000Z`).toISOString();
}

function getStatusOptions(currentStatus?: DriverStatus) {
  if (currentStatus === DriverStatus.已驗證) {
    return [DriverStatus.已驗證, DriverStatus.停用中];
  }
  if (currentStatus === DriverStatus.停用中) {
    return [DriverStatus.停用中];
  }
  return [DriverStatus.未驗證, DriverStatus.已驗證, DriverStatus.停用中];
}

export default function DriverProfileEdit() {
  const navigate = useNavigate();
  const { userUID } = useParams();
  const authUser = authUserData();
  const parsedUserUID = Number(userUID);

  const [user, setUser] = useState<User | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [editedDriver, setEditedDriver] = useState<Driver | null>(null);
  const [vehicleTypes, setVehicleTypes] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [photoUploading, setPhotoUploading] = useState({
    license: false,
    motorLicense: false,
    insurance: false,
  });
  const [photoUploadErrors, setPhotoUploadErrors] = useState<{
    license?: string;
    motorLicense?: string;
    insurance?: string;
  }>({});
  const [driverPhotoUrls, setDriverPhotoUrls] = useState<DriverPhotoUrls>({
    licensePhotoUrl: null,
    motorLicensePhotoUrl: null,
    insurancePhotoUrl: null,
  });

  useEffect(() => {
    if (authUser?.team?.vehicleData) {
      try {
        setVehicleTypes(JSON.parse(authUser.team.vehicleData));
      } catch (parseError) {
        console.error("Error parsing vehicle data:", parseError);
      }
    }
  }, [authUser]);

  const updateDriverPhotoUrls = useCallback((next: Partial<DriverPhotoUrls>) => {
    setDriverPhotoUrls((prev) => {
      const updated = { ...prev, ...next };
      if (prev.licensePhotoUrl && prev.licensePhotoUrl !== updated.licensePhotoUrl) {
        URL.revokeObjectURL(prev.licensePhotoUrl);
      }
      if (
        prev.motorLicensePhotoUrl &&
        prev.motorLicensePhotoUrl !== updated.motorLicensePhotoUrl
      ) {
        URL.revokeObjectURL(prev.motorLicensePhotoUrl);
      }
      if (prev.insurancePhotoUrl && prev.insurancePhotoUrl !== updated.insurancePhotoUrl) {
        URL.revokeObjectURL(prev.insurancePhotoUrl);
      }
      return updated;
    });
  }, []);

  const loadDriverProfile = useCallback(async () => {
    if (!authUser) {
      navigate("/login");
      return;
    }

    if (!Number.isInteger(parsedUserUID) || parsedUserUID <= 0) {
      setError("司機資料參數無效");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [loadedUser, loadedDriver] = await Promise.all([
        UserService.getUserById(parsedUserUID),
        getDriver(parsedUserUID),
      ]);

      if (!loadedDriver) {
        throw new Error("找不到司機資料");
      }

      setUser(loadedUser);
      setDriver(loadedDriver);
      setEditedDriver({ ...loadedDriver });
    } catch (err) {
      console.error("Load driver profile error:", err);
      setError(err instanceof Error ? err.message : "載入司機資料失敗");
    } finally {
      setLoading(false);
    }
  }, [authUser, navigate, parsedUserUID]);

  useEffect(() => {
    void loadDriverProfile();
  }, [loadDriverProfile]);

  useEffect(() => {
    if (!driver) {
      updateDriverPhotoUrls({
        licensePhotoUrl: null,
        motorLicensePhotoUrl: null,
        insurancePhotoUrl: null,
      });
      return;
    }

    let isActive = true;
    const fetchPhotos = async () => {
      const [licensePhotoUrl, motorLicensePhotoUrl, insurancePhotoUrl] = await Promise.all([
        driver.carLicensePhoto ? getDriverPhoto(driver.carLicensePhoto) : Promise.resolve(null),
        driver.motorLicensePhoto
          ? getDriverPhoto(driver.motorLicensePhoto)
          : Promise.resolve(null),
        driver.insurancePhoto ? getDriverPhoto(driver.insurancePhoto) : Promise.resolve(null),
      ]);

      if (!isActive) {
        if (licensePhotoUrl) {
          URL.revokeObjectURL(licensePhotoUrl);
        }
        if (motorLicensePhotoUrl) {
          URL.revokeObjectURL(motorLicensePhotoUrl);
        }
        if (insurancePhotoUrl) {
          URL.revokeObjectURL(insurancePhotoUrl);
        }
        return;
      }

      updateDriverPhotoUrls({ licensePhotoUrl, motorLicensePhotoUrl, insurancePhotoUrl });
    };

    void fetchPhotos();

    return () => {
      isActive = false;
    };
  }, [driver, updateDriverPhotoUrls]);

  useEffect(() => {
    return () => {
      updateDriverPhotoUrls({
        licensePhotoUrl: null,
        motorLicensePhotoUrl: null,
        insurancePhotoUrl: null,
      });
    };
  }, [updateDriverPhotoUrls]);

  const handleDriverPhotoUpload = async (
    file: File | null,
    fileType: "CarLicensePhoto" | "MotorLicensePhoto" | "InsurancePhoto"
  ) => {
    if (!file || !editedDriver) {
      return;
    }

    const isLicense = fileType === "CarLicensePhoto";
    const isMotorLicense = fileType === "MotorLicensePhoto";
    const photoKey = isLicense ? "license" : isMotorLicense ? "motorLicense" : "insurance";

    setPhotoUploading((prev) => ({ ...prev, [photoKey]: true }));
    setPhotoUploadErrors((prev) => ({ ...prev, [photoKey]: undefined }));

    try {
      const result = await uploadDriverPhoto(file, fileType, editedDriver.userUID);
      const fileName = result?.fileName;
      if (!fileName) {
        throw new Error("上傳失敗");
      }

      const updatedDriver = {
        ...editedDriver,
        ...(isLicense
          ? { carLicensePhoto: fileName }
          : isMotorLicense
            ? { motorLicensePhoto: fileName }
            : { insurancePhoto: fileName }),
      };

      setEditedDriver(updatedDriver);
      setDriver((prev) => (prev ? { ...prev, ...updatedDriver } : prev));

      const localPreviewUrl = URL.createObjectURL(file);
      updateDriverPhotoUrls(
        isLicense
          ? { licensePhotoUrl: localPreviewUrl }
          : isMotorLicense
            ? { motorLicensePhotoUrl: localPreviewUrl }
            : { insurancePhotoUrl: localPreviewUrl }
      );
    } catch (uploadError) {
      const errorMessage = uploadError instanceof Error ? uploadError.message : "上傳失敗";
      setPhotoUploadErrors((prev) => ({ ...prev, [photoKey]: errorMessage }));
    } finally {
      setPhotoUploading((prev) => ({ ...prev, [photoKey]: false }));
    }
  };

  const statusOptions = useMemo(
    () =>
      getStatusOptions(driver?.status).map((status) => ({
        label: DriverStatus[status],
        value: status,
      })),
    [driver]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicleTypes.map((vehicle) => ({
        label: `${vehicle.vehiclename} (${vehicle.vehicleID})`,
        value: vehicle.vehicleID,
      })),
    [vehicleTypes]
  );

  const handleSave = async () => {
    if (!driver || !editedDriver) {
      return;
    }

    setError("");
    setSuccessMessage("");

    if (!editedDriver.driverName.trim()) {
      setError("請輸入司機姓名");
      return;
    }
    if (!editedDriver.phoneNumber.trim()) {
      setError("請輸入電話");
      return;
    }
    if (!editedDriver.licencePlateNumber.trim()) {
      setError("請輸入車牌號碼");
      return;
    }
    if (!editedDriver.vehicleID?.trim()) {
      setError("請選擇車種");
      return;
    }

    const originalStatus = driver.status;
    const nextStatus = editedDriver.status;

    if (
      originalStatus === DriverStatus.已驗證 &&
      nextStatus !== DriverStatus.已驗證 &&
      nextStatus !== DriverStatus.停用中
    ) {
      setError("司機核可後僅能維持已驗證或改為停用中");
      return;
    }

    if (originalStatus === DriverStatus.停用中 && nextStatus !== DriverStatus.停用中) {
      setError("停用中的司機不可重新調整為其他狀態");
      return;
    }

    const payload: Driver = {
      ...editedDriver,
      status: originalStatus,
      idCardNo: editedDriver.idCardNo?.trim() || "",
      bankID: editedDriver.bankID?.trim() || null,
      bankAccount: editedDriver.bankAccount?.trim() || null,
      address: editedDriver.address?.trim() || "",
      currentMileage: Number(editedDriver.currentMileage ?? 0) || 0,
      carCheckDate: toIsoDateOrNull(toDateInputValue(editedDriver.carCheckDate)) as unknown as Date | null,
      insuranceExpiryDate: toIsoDateOrNull(
        toDateInputValue(editedDriver.insuranceExpiryDate)
      ) as unknown as Date | null,
      linceseExpiryDate: toIsoDateOrNull(
        toDateInputValue(editedDriver.linceseExpiryDate)
      ) as unknown as Date | null,
      driverName: editedDriver.driverName.trim(),
      licencePlateNumber: editedDriver.licencePlateNumber.trim(),
      phoneNumber: editedDriver.phoneNumber.trim(),
      vehicleID: editedDriver.vehicleID.trim(),
      carBrand: editedDriver.carBrand?.trim() || "",
      carModel: editedDriver.carModel?.trim() || "",
      carColor: editedDriver.carColor?.trim() || "",
    };

    try {
      setSaving(true);
      await updateDriver(payload);

      if (originalStatus === DriverStatus.未驗證 && nextStatus === DriverStatus.已驗證) {
        await approveApply(editedDriver.userUID);
      } else if (
        nextStatus === DriverStatus.停用中 &&
        originalStatus !== DriverStatus.停用中
      ) {
        await disableDriver(editedDriver.userUID);
      }

      const refreshedDriver = await getDriver(editedDriver.userUID);
      const refreshedUser = await UserService.getUserById(editedDriver.userUID);

      if (!refreshedDriver) {
        throw new Error("儲存後無法取得司機資料");
      }

      setDriver(refreshedDriver);
      setEditedDriver({ ...refreshedDriver });
      setUser(refreshedUser);
      setSuccessMessage(
        originalStatus === DriverStatus.未驗證 && nextStatus === DriverStatus.已驗證
          ? "司機資料已更新，並已核可申請與發送推播通知"
          : "司機資料已更新"
      );
    } catch (err) {
      console.error("Save driver profile error:", err);
      setError(err instanceof Error ? err.message : "更新司機資料失敗");
    } finally {
      setSaving(false);
    }
  };

  const driverPhotoStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 240,
    maxHeight: 160,
    objectFit: "contain",
    borderRadius: 4,
    border: "1px solid #e5e5e5",
  };

  return (
    <AdminDashboard title="編輯司機資料">
      <Stack justifyContent="space-between" alignItems="center" wrap spacing={12} style={{ marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0 }}>司機資料編輯</h4>
          <p style={{ margin: "8px 0 0", color: "#666" }}>
            {user ? `${user.displayName} / ${user.email || "無電子郵件"}` : `User UID: ${userUID}`}
          </p>
        </div>
        <Button appearance="ghost" onClick={() => navigate(-1)}>
          返回
        </Button>
      </Stack>

      {error && (
        <Message type="error" style={{ marginBottom: 16 }}>
          {error}
        </Message>
      )}

      {successMessage && (
        <Message type="success" style={{ marginBottom: 16 }}>
          {successMessage}
        </Message>
      )}

      {loading ? (
        <Panel bordered bodyFill style={{ padding: 40, textAlign: "center" }}>
          <Loader size="lg" content="載入司機資料中..." />
        </Panel>
      ) : editedDriver ? (
        <Panel bordered>
          <Form fluid>
            <h2 className="fleet-form-section">基本資料與收款資訊</h2>
            <Row gutter={16}>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>申請狀態</Form.ControlLabel>
                  <SelectPicker
                    data={statusOptions}
                    value={editedDriver.status}
                    cleanable={false}
                    searchable={false}
                    block
                    onChange={(value) =>
                      setEditedDriver((prev) =>
                        prev ? { ...prev, status: value as DriverStatus } : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>車種</Form.ControlLabel>
                  <SelectPicker
                    data={vehicleOptions}
                    value={editedDriver.vehicleID}
                    cleanable={false}
                    searchable={false}
                    block
                    onChange={(value) =>
                      setEditedDriver((prev) => (prev ? { ...prev, vehicleID: String(value) } : prev))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>姓名</Form.ControlLabel>
                  <Form.Control
                    name="driverName"
                    value={editedDriver.driverName}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, driverName: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>電話</Form.ControlLabel>
                  <Form.Control
                    name="phoneNumber"
                    value={editedDriver.phoneNumber}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, phoneNumber: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>車號</Form.ControlLabel>
                  <Form.Control
                    name="licencePlateNumber"
                    value={editedDriver.licencePlateNumber}
                    onChange={(value: string) =>
                      setEditedDriver((prev) =>
                        prev ? { ...prev, licencePlateNumber: value } : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>身分證號</Form.ControlLabel>
                  <Form.Control
                    name="idCardNo"
                    value={editedDriver.idCardNo ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, idCardNo: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>銀行代碼</Form.ControlLabel>
                  <Form.Control
                    name="bankID"
                    value={editedDriver.bankID ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, bankID: value || null } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.ControlLabel>銀行帳號</Form.ControlLabel>
                  <Form.Control
                    name="bankAccount"
                    value={editedDriver.bankAccount ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) =>
                        prev ? { ...prev, bankAccount: value || null } : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <Form.ControlLabel>地址</Form.ControlLabel>
              <Form.Control
                name="address"
                value={editedDriver.address ?? ""}
                onChange={(value: string) =>
                  setEditedDriver((prev) => (prev ? { ...prev, address: value } : prev))
                }
              />
            </Form.Group>

            <Row gutter={16}>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>廠牌</Form.ControlLabel>
                  <Form.Control
                    name="carBrand"
                    value={editedDriver.carBrand ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, carBrand: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>車型</Form.ControlLabel>
                  <Form.Control
                    name="carModel"
                    value={editedDriver.carModel ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, carModel: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>車色</Form.ControlLabel>
                  <Form.Control
                    name="carColor"
                    value={editedDriver.carColor ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, carColor: value } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>目前里程</Form.ControlLabel>
                  <Form.Control
                    name="currentMileage"
                    value={editedDriver.currentMileage ?? ""}
                    onChange={(value: string) =>
                      setEditedDriver((prev) =>
                        prev
                          ? { ...prev, currentMileage: value === "" ? 0 : Number(value) }
                          : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>驗車日期</Form.ControlLabel>
                  <Form.Control
                    name="carCheckDate"
                    accepter={Input}
                    type="date"
                    value={toDateInputValue(editedDriver.carCheckDate)}
                    onChange={(value: string) =>
                      setEditedDriver((prev) => (prev ? { ...prev, carCheckDate: value as unknown as Date } : prev))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>保險到期日</Form.ControlLabel>
                  <Form.Control
                    name="insuranceExpiryDate"
                    accepter={Input}
                    type="date"
                    value={toDateInputValue(editedDriver.insuranceExpiryDate)}
                    onChange={(value: string) =>
                      setEditedDriver((prev) =>
                        prev ? { ...prev, insuranceExpiryDate: value as unknown as Date } : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>駕照到期日</Form.ControlLabel>
                  <Form.Control
                    name="linceseExpiryDate"
                    accepter={Input}
                    type="date"
                    value={toDateInputValue(editedDriver.linceseExpiryDate)}
                    onChange={(value: string) =>
                      setEditedDriver((prev) =>
                        prev ? { ...prev, linceseExpiryDate: value as unknown as Date } : prev
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <h2 className="fleet-form-section">稅務繳納紀錄</h2>

            <Row gutter={16}>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.fuelTaxMarch}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) => (prev ? { ...prev, fuelTaxMarch: checked } : prev))
                  }
                >
                  燃料稅 3 月
                </Checkbox>
              </Col>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.fuelTaxJune}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) => (prev ? { ...prev, fuelTaxJune: checked } : prev))
                  }
                >
                  燃料稅 6 月
                </Checkbox>
              </Col>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.fuelTaxSeptember}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) =>
                      prev ? { ...prev, fuelTaxSeptember: checked } : prev
                    )
                  }
                >
                  燃料稅 9 月
                </Checkbox>
              </Col>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.fuelTaxDecember}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) =>
                      prev ? { ...prev, fuelTaxDecember: checked } : prev
                    )
                  }
                >
                  燃料稅 12 月
                </Checkbox>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.licenceTaxJune}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) => (prev ? { ...prev, licenceTaxJune: checked } : prev))
                  }
                >
                  行照稅 6 月
                </Checkbox>
              </Col>
              <Col md={6}>
                <Checkbox
                  checked={editedDriver.licenceTaxOctober}
                  onChange={(_, checked) =>
                    setEditedDriver((prev) =>
                      prev ? { ...prev, licenceTaxOctober: checked } : prev
                    )
                  }
                >
                  行照稅 10 月
                </Checkbox>
              </Col>
            </Row>

            <h2 className="fleet-form-section">證件與保險文件</h2>

            <Row gutter={16}>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>駕照照片</Form.ControlLabel>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={photoUploading.license}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      void handleDriverPhotoUpload(file, "CarLicensePhoto");
                      event.target.value = "";
                    }}
                  />
                  {photoUploading.license && <p style={{ color: "#999", marginTop: 6 }}>上傳中...</p>}
                  {photoUploadErrors.license && (
                    <p style={{ color: "#c0392b", marginTop: 6 }}>{photoUploadErrors.license}</p>
                  )}
                  {driverPhotoUrls.licensePhotoUrl ? (
                    <img src={driverPhotoUrls.licensePhotoUrl} alt="行照照片" style={{ ...driverPhotoStyle, marginTop: 8 }} />
                  ) : (
                    <p style={{ color: "#999", marginTop: 6 }}>未提供</p>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>行車執照照片</Form.ControlLabel>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={photoUploading.motorLicense}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      void handleDriverPhotoUpload(file, "MotorLicensePhoto");
                      event.target.value = "";
                    }}
                  />
                  {photoUploading.motorLicense && (
                    <p style={{ color: "#999", marginTop: 6 }}>上傳中...</p>
                  )}
                  {photoUploadErrors.motorLicense && (
                    <p style={{ color: "#c0392b", marginTop: 6 }}>
                      {photoUploadErrors.motorLicense}
                    </p>
                  )}
                  {driverPhotoUrls.motorLicensePhotoUrl ? (
                    <img
                      src={driverPhotoUrls.motorLicensePhotoUrl}
                      alt="行車執照照片"
                      style={{ ...driverPhotoStyle, marginTop: 8 }}
                    />
                  ) : (
                    <p style={{ color: "#999", marginTop: 6 }}>未提供</p>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>保險證</Form.ControlLabel>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={photoUploading.insurance}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      void handleDriverPhotoUpload(file, "InsurancePhoto");
                      event.target.value = "";
                    }}
                  />
                  {photoUploading.insurance && (
                    <p style={{ color: "#999", marginTop: 6 }}>上傳中...</p>
                  )}
                  {photoUploadErrors.insurance && (
                    <p style={{ color: "#c0392b", marginTop: 6 }}>{photoUploadErrors.insurance}</p>
                  )}
                  {driverPhotoUrls.insurancePhotoUrl ? (
                    <img
                      src={driverPhotoUrls.insurancePhotoUrl}
                      alt="保險證"
                      style={{ ...driverPhotoStyle, marginTop: 8 }}
                    />
                  ) : (
                    <p style={{ color: "#999", marginTop: 6 }}>未提供</p>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Stack spacing={12} style={{ marginTop: 24 }}>
              <Button appearance="primary" loading={saving} onClick={() => void handleSave()}>
                儲存司機資料
              </Button>
              <Button appearance="ghost" onClick={() => navigate("/drivers/approval")}>
                返回待核可列表
              </Button>
            </Stack>
          </Form>
        </Panel>
      ) : (
        <Message type="warning">找不到司機資料</Message>
      )}
    </AdminDashboard>
  );
}
